/**
 * Daggerheart PC abilities (class / ancestry / community / domain / note).
 * Rendered as a full-width name + description table with a rule underneath.
 */

import {
  ANCESTRIES,
  CLASSES,
  COMMUNITIES,
  LEVEL_1_DOMAIN_CARDS,
  type SrdClass,
} from "./daggerheartSrd2";

export type PcAbility = {
  name: string;
  text: string;
};

const ABILITY_PROP_RE =
  /^(features|class[_-]?features|feats|abilities|domain[_-]?cards)$/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function textOf(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return value == null ? "" : String(value).trim();
}

function lookupByName<T extends { name: string }>(
  table: Record<string, T>,
  raw: string,
): T | undefined {
  const name = raw.trim();
  if (!name) return undefined;
  if (table[name]) return table[name];
  const key = Object.keys(table).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? table[key] : undefined;
}

function lookupClass(raw: string): SrdClass | undefined {
  return lookupByName(CLASSES, raw);
}

function lookupDomainCard(raw: string) {
  const name = raw.trim();
  if (!name) return undefined;
  return LEVEL_1_DOMAIN_CARDS.find(
    (card) => card.name.toLowerCase() === name.toLowerCase(),
  );
}

function withDiceLinks(raw: string): string {
  const escaped = escapeHtml(raw);
  return escaped.replace(/\b(\d+)?d(\d+)(?:\s*[+-]\s*\d+)?\b/gi, (match) => {
    const formula = match.replace(/\s+/g, "");
    return `<span class="atlas-dice-link dh-adversary-dice-btn" data-dh-roll="dice" data-dh-roll-payload="${escapeHtml(formula)}" role="button" tabindex="0" title="Click to roll ${escapeHtml(formula)}">${match}</span>`;
  });
}

function pushAbility(out: PcAbility[], seen: Set<string>, ability: PcAbility | null): void {
  if (!ability || !ability.name) return;
  const key = ability.name.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push({ name: ability.name.trim(), text: ability.text.trim() });
}

function parseAbilityList(raw: unknown): PcAbility[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return parseAbilityLine(item);
        if (item && typeof item === "object") {
          const rec = item as Record<string, unknown>;
          const name = textOf(rec.name ?? rec.title);
          const text = textOf(rec.text ?? rec.description ?? rec.value);
          return name ? { name, text } : parseAbilityLine(textOf(item));
        }
        return null;
      })
      .filter((row): row is PcAbility => !!row && !!row.name);
  }
  const blob = textOf(raw);
  if (!blob) return [];
  return blob
    .split(/\n+|;\s+/)
    .map((line) => parseAbilityLine(line))
    .filter((row): row is PcAbility => !!row && !!row.name);
}

function parseAbilityLine(line: string): PcAbility | null {
  const text = line.replace(/\s+/g, " ").trim();
  if (!text) return null;
  const split = /^(.*?)(?:\s*[—–:]\s+|\.\s+)(.+)$/.exec(text);
  if (split?.[1] && split[2] && split[1].length <= 48) {
    return { name: split[1].trim(), text: split[2].trim() };
  }
  const srd = lookupDomainCard(text);
  if (srd) return { name: srd.name, text: srd.text };
  return { name: text, text: "" };
}

function splitNames(raw: string): string[] {
  return raw
    .split(/\s*,\s*|\s*;\s*|\n+/)
    .map((part) => part.replace(/\s*\[[^\]]*\]\s*/g, "").trim())
    .filter(Boolean);
}

function ancestryNames(raw: string): string[] {
  const mixed = /mixed\s*\(([^)]+)\)/i.exec(raw);
  if (mixed?.[1]) return splitNames(mixed[1].replace(/\//g, ","));
  return splitNames(raw);
}

export function collectPcAbilities(creature: Record<string, unknown>): PcAbility[] {
  const out: PcAbility[] = [];
  const seen = new Set<string>();

  for (const key of ["features", "class_features", "feats", "abilities"]) {
    for (const row of parseAbilityList(creature[key])) {
      const srd = lookupDomainCard(row.name);
      pushAbility(out, seen, {
        name: row.name,
        text: row.text || srd?.text || "",
      });
    }
  }

  const cls = lookupClass(textOf(creature.class));
  if (cls) {
    pushAbility(out, seen, {
      name: cls.hopeFeature.name,
      text: cls.hopeFeature.text,
    });
    for (const feat of cls.classFeatures) {
      pushAbility(out, seen, feat);
    }
    const subclassName = textOf(creature.subclass);
    const subclass = cls.subclasses.find(
      (entry) => entry.name.toLowerCase() === subclassName.toLowerCase(),
    );
    if (subclass?.foundation) {
      pushAbility(out, seen, subclass.foundation);
    }
  }

  for (const name of ancestryNames(textOf(creature.ancestry))) {
    const ancestry = lookupByName(ANCESTRIES, name);
    if (!ancestry) continue;
    pushAbility(out, seen, ancestry.feature1);
    pushAbility(out, seen, ancestry.feature2);
  }

  const community = lookupByName(COMMUNITIES, textOf(creature.community));
  if (community) pushAbility(out, seen, community.feature);

  for (const name of splitNames(textOf(creature.domain_cards))) {
    const card = lookupDomainCard(name);
    pushAbility(out, seen, card ? { name: card.name, text: card.text } : { name, text: "" });
  }

  return out;
}

export function isAbilityProperty(label: string): boolean {
  return ABILITY_PROP_RE.test(label.trim());
}

export function abilityTableHtml(abilities: PcAbility[]): string {
  if (abilities.length === 0) return "";
  const rows = abilities
    .map((ability) => {
      const name = escapeHtml(ability.name);
      const text = ability.text ? withDiceLinks(ability.text) : "";
      return `<tr><td class="dh-pc-ability-name">${name}</td><td class="dh-pc-ability-text">${text}</td></tr>`;
    })
    .join("");
  return `<div class="statblock-item-container dh-pc-abilities" data-dh-pc-abilities="1"><div class="section-header">Features</div><table class="dh-pc-abilities-table"><tbody>${rows}</tbody></table><hr class="dh-pc-abilities-rule" /></div>`;
}

function isAbilityLine(el: Element): boolean {
  const prop = el.getAttribute("data-property") || "";
  if (ABILITY_PROP_RE.test(prop)) return true;
  const cls = typeof el.className === "string" ? el.className : "";
  if (/\b(features|class-features|feats|abilities|domain-cards)\b/i.test(cls)) return true;
  const heading = el.matches(".section-header, .statblock-item-heading")
    ? el.textContent || ""
    : el.querySelector?.(".section-header, .statblock-item-heading")?.textContent || "";
  if (/^(features|abilities|domain cards)$/i.test(heading.trim())) return true;
  const name = el.querySelector?.(".property-name")?.textContent?.trim() || "";
  return /^(features|class features|feats|abilities|domain cards)$/i.test(name);
}

function collectAbilityWraps(root: Element): Element[] {
  const seen = new Set<Element>();
  const wraps: Element[] = [];
  root
    .querySelectorAll(
      "[data-dh-pc-abilities='1'], [data-property='features'], [data-property='class-features'], [data-property='feats'], [data-property='abilities'], [data-property='domain-cards'], .line.features, .line.domain-cards, .features, .statblock-trait-prop",
    )
    .forEach((el) => {
      if (!isAbilityLine(el) && !el.matches("[data-dh-pc-abilities='1']")) return;
      const wrap = el.closest(".statblock-item-container") ?? el;
      if (seen.has(wrap)) return;
      seen.add(wrap);
      wraps.push(wrap);
    });
  return wraps;
}

function abilitiesFromDom(wraps: Element[]): PcAbility[] {
  const out: PcAbility[] = [];
  const seen = new Set<string>();
  for (const wrap of wraps) {
    if (wrap.getAttribute("data-dh-pc-abilities") === "1") {
      wrap.querySelectorAll("tr").forEach((row) => {
        const name = row.querySelector(".dh-pc-ability-name")?.textContent?.trim() || "";
        const text = row.querySelector(".dh-pc-ability-text")?.textContent?.trim() || "";
        pushAbility(out, seen, name ? { name, text } : null);
      });
      continue;
    }
    const name =
      wrap.querySelector(".property-name")?.textContent?.trim() ||
      wrap.querySelector(".statblock-rendered-text-content")?.textContent?.trim() ||
      "";
    const text = (
      wrap.querySelector(".property-text")?.textContent ||
      wrap.querySelector("p")?.textContent ||
      ""
    ).trim();
    if (/^(features|class features|feats|abilities|domain cards)$/i.test(name)) {
      for (const row of parseAbilityList(text)) pushAbility(out, seen, row);
      continue;
    }
    if (name) pushAbility(out, seen, { name, text });
  }
  return out;
}

function propertyValueFromDom(root: Element, label: string): string {
  const want = label.toLowerCase();
  for (const nameEl of root.querySelectorAll(".property-name")) {
    const labelText = (nameEl.textContent || "").replace(/:$/, "").trim().toLowerCase();
    if (labelText !== want) continue;
    const line = nameEl.closest(".line") ?? nameEl.parentElement;
    const value =
      line?.querySelector(".property-text")?.textContent?.trim() ||
      nameEl.nextElementSibling?.textContent?.trim() ||
      "";
    if (value) return value;
  }
  return "";
}

function creatureFromSheetDom(
  creature: Record<string, unknown>,
  root: Element,
): Record<string, unknown> {
  const next = { ...creature };
  for (const key of ["class", "subclass", "ancestry", "community", "features", "domain_cards"]) {
    if (textOf(next[key])) continue;
    const fromHtml = propertyValueFromDom(root, key.replace(/_/g, " "));
    if (fromHtml) next[key] = fromHtml;
  }
  return next;
}

function insertAfterAnchor(root: Element, html: string): void {
  const column =
    root.querySelector(".column") ||
    root.querySelector(".statblock-content") ||
    root;
  const endRule =
    root.querySelector("[data-dh-pc-stats-end]") ||
    root.querySelector("hr.dh-pc-stats-rule-end") ||
    root.querySelector(".dh-pc-stats-rule-end");
  if (endRule?.parentNode) {
    endRule.insertAdjacentHTML("afterend", html);
    return;
  }
  const prefs = [
    ".dh-pc-stats",
    "[data-dh-pc-weapon='1']",
    "[data-property='knowledge']",
    ".line.knowledge",
    "[data-property='hope']",
    ".line.hope",
  ];
  for (const sel of prefs) {
    const matches = [...root.querySelectorAll(sel)];
    const last = matches[matches.length - 1];
    if (!last) continue;
    const wrap = last.closest(".statblock-item-container") ?? last;
    if (wrap.parentNode) {
      wrap.insertAdjacentHTML("afterend", html);
      return;
    }
  }
  column.insertAdjacentHTML("beforeend", html);
}

export function ensureAbilityTableInFsbHtml(
  html: string,
  creature: Record<string, unknown>,
): string {
  if (!html || !html.includes("<")) return html;
  const fromCreature = collectPcAbilities(creature);
  try {
    if (typeof DOMParser !== "undefined") {
      const doc = new DOMParser().parseFromString(
        `<div id="fsb-root">${html}</div>`,
        "text/html",
      );
      const root = doc.getElementById("fsb-root");
      if (root) {
        const existing = collectAbilityWraps(root);
        const fromDom = abilitiesFromDom(existing);
        const merged: PcAbility[] = [];
        const seen = new Set<string>();
        const filled = collectPcAbilities(creatureFromSheetDom(creature, root));
        for (const row of [...fromDom, ...fromCreature, ...filled]) pushAbility(merged, seen, row);
        existing.forEach((el) => el.remove());
        const table = abilityTableHtml(merged);
        if (table) insertAfterAnchor(root, table);
        return root.innerHTML.trim();
      }
    }
  } catch {
    /* fall through */
  }
  const harvested: Record<string, unknown> = { ...creature };
  const propRe =
    /<span class="property-name">\s*(class|subclass|ancestry|community|features|domain cards):?\s*<\/span>\s*<span class="property-text">([^<]*)<\/span>/gi;
  let propHit: RegExpExecArray | null;
  while ((propHit = propRe.exec(html))) {
    const key = propHit[1]!.toLowerCase().replace(/\s+/g, "_");
    if (!textOf(harvested[key]) && propHit[2]?.trim()) {
      harvested[key] = propHit[2].trim();
    }
  }
  const table = abilityTableHtml(collectPcAbilities(harvested));
  if (!table) return html;
  const stripped = html
    .replace(
      /<div class="statblock-item-container dh-pc-abilities"[\s\S]*?<\/table>\s*<hr[^>]*>\s*<\/div>/gi,
      "",
    )
    .replace(
      /<div class="statblock-item-container"[^>]*>\s*<div class="line[^"]*"[^>]*(?:data-property="(?:features|class-features|feats|abilities|domain-cards)"|class="line (?:features|class-features|feats|abilities|domain-cards)")[^>]*>[\s\S]*?<\/div>\s*<\/div>/gi,
      "",
    );
  const endRule = /<hr[^>]*(?:data-dh-pc-stats-end|dh-pc-stats-rule-end)[^>]*>/i.exec(
    stripped,
  );
  if (endRule && endRule.index != null) {
    const end = endRule.index + endRule[0].length;
    return `${stripped.slice(0, end)}${table}${stripped.slice(end)}`;
  }
  const statsClose = stripped.search(/<\/div>\s*<hr[^>]*dh-pc-stats-rule-end/i);
  if (statsClose >= 0) {
    return `${stripped.slice(0, statsClose)}${table}${stripped.slice(statsClose)}`;
  }
  return `${stripped}${table}`;
}

export function insertAbilityTableInDom(
  container: HTMLElement,
  creature: Record<string, unknown>,
): void {
  const existing = collectAbilityWraps(container);
  const fromDom = abilitiesFromDom(existing);
  const fromCreature = collectPcAbilities(creatureFromSheetDom(creature, container));
  const merged: PcAbility[] = [];
  const seen = new Set<string>();
  for (const row of [...fromDom, ...fromCreature]) pushAbility(merged, seen, row);
  existing.forEach((el) => el.remove());
  const table = abilityTableHtml(merged);
  if (!table) return;
  insertAfterAnchor(container, table);
}
